/**
 * The sign-in gate — client review R1 (2026-08-22), rebuilt to the client's mock by **C5**.
 *
 * The geoportal's **first** page: a session goes gate → §F4 splash → app. It is an overlay
 * on the same pattern as the splash (a sibling of `<AppRoutes/>` in `App.tsx`, never a
 * route) for the same reason — the app behind it must stay mounted, so that dismissing the
 * gate is a paint and not a boot — and it suppresses itself for the session under its own
 * `sessionStorage` key (`GATE_SEEN_KEY`, `lib/state.ts`).
 *
 * ## C5: a page, not a card
 *
 * R1 shipped a 940 px centred card. The client's mock is a full-bleed two-panel page, and the
 * difference is not decoration: the left panel is where the programme introduces itself and the
 * right half is the white form: logo, *Welcome*, email, password with a reveal toggle, the two
 * inert conveniences (Remember me, Forgot password?), and the button.
 *
 * ## 2026-10-08: the photograph, a scrim, and two sentences
 *
 * The frosted sheet the 2026-10-06 round shipped (title beside four country pills, a subtitle,
 * a lede, the three use tiles, a waved tagline band) is gone — client: *"remove the whole soil
 * values geoportal card section, place a dark overlay on the image there"*. The left panel is
 * now the field photograph (`gateAssets.BACKDROP`) under one dark scrim for legibility, carrying
 * only the programme's name and two sentences of standing copy (`gate.overviewP1`/`P2`), both
 * white and centred. The right half is unchanged: the form on plain white, the attribution in a
 * bordered card at its foot. All of it is in `base.css` under *the sign-in gate*.
 *
 * ## The fields are inert, and that is the specification, not a stub
 *
 * The review asks for an email and a password that are *visible* and cost nothing: "no
 * validation, no credentials required, permissions come later". So they are real, focusable,
 * typeable inputs whose values are never read, never sent and never stored — uncontrolled,
 * outside React state entirely, with `autoComplete="off"` so no browser offers to save a
 * credential this app has no account to check it against. Two rejected alternatives, both
 * worse:
 *
 * * **`disabled` fields.** They would *look* like the gate is broken or still loading, and
 *   a reader who cannot click into an email box reads it as a fault to report.
 * * **A fake check.** Any validation at all — a required marker, a "wrong password"
 *   message, a spinner — would be a claim about an authentication system that does not
 *   exist yet. §I2's real one arrives with roles; until then the honest UI is one that lets
 *   anyone in. (A line under the buttons used to say so in words; the client removed it on
 *   2026-10-06, and *Continue as guest* now carries that message on its own.)
 *
 * *Remember me* and *Forgot password?* join them: the mock has both, and both are inert for
 * exactly the same reason — there is no session to remember and no address to send a reset
 * to. They are marked `aria-disabled` rather than `disabled` so a screen reader hears why
 * instead of hearing nothing, and each carries a `title` (`gate.inertControl`) saying so.
 *
 * ## "Continue as guest" stays, and the mock omits it
 *
 * The client's mock has *Sign In* and nothing else. Taken literally that would leave the only
 * way into a portal whose data is entirely open behind a button that authenticates nobody —
 * and would make the honest action (browsing as a guest) the one with no control. So the door
 * survives. Logged as a deviation in docs/NOTES.md.
 *
 * C5 demoted it to a discreet underlined line, and the 2026-09-02 review put it back to a
 * full-width button: until §I2 ships there is no account to sign in with, so the line of
 * 12 px text was the control that actually worked and the solid blue button was the one that
 * did nothing. It stays visually second — soft green fill, outlined, against Sign In's solid
 * blue — which is the mock's hierarchy without a 12 px tap target at the bottom of a phone.
 *
 * Both exits therefore still do exactly one thing, and the same thing: `setGateOpen(false)`.
 *
 * **ESC does not close it.** Deliberate, and the one place this overlay departs from the
 * splash's keyboard contract: the splash is a thing you dismiss, the gate is a thing you
 * pass through, and there is no hidden shortcut past it. Every exit is a labelled control.
 *
 * **It is a real modal, not just a `role` (R6 fix).** `aria-modal="true"` is a claim about
 * the whole document — it tells assistive tech to ignore everything outside this node — and a
 * dialog that makes that claim without moving focus into itself leaves a screen-reader user's
 * first screen of the session showing one dialog with their cursor nowhere in it. So on open:
 * focus lands on the email field, the rest of the document is marked `inert` (which also
 * takes the covered topbar and map controls out of the Tab order a sighted keyboard user was
 * walking through), and Tab cycles inside the page. On close, focus goes back where it came
 * from. §F2's `#toast` is deliberately left out of the `inert` sweep: it is the app's one
 * `aria-live` region, and a boot-time message (`PermalinkSync`'s "some shared settings were
 * not recognised") must still be announced to a reader who is standing at the gate.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../lib/icons';
import { asLanguage, DEFAULT_LANGUAGE, otherLanguage, setLanguage } from '../../lib/i18n';
import { useAppStore } from '../../lib/state';
import { BACKDROP, LOGO, NETHERLANDS, NETHERLANDS_HREF, PARTNERS } from './gateAssets';

/**
 * Everything inside the page a Tab can reach, in document order.
 *
 * Queried on each keypress rather than cached: the contents change under the reader (the
 * EN/FR button re-renders the whole form, the reveal toggle swaps its own icon), and a stale
 * list would trap Tab on a node that is no longer there.
 */
const TABBABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function SignInGate() {
  const { t, i18n } = useTranslation();
  const open = useAppStore((s) => s.gateOpen);
  const setGateOpen = useAppStore((s) => s.setGateOpen);
  /**
   * Read off the i18next instance, as the topbar's toggle is, so it re-renders on
   * `languageChanged` whoever caused it.
   */
  const language = asLanguage(i18n.resolvedLanguage ?? i18n.language) ?? DEFAULT_LANGUAGE;

  /** The mock's eye toggle. The one piece of state on this page, and it is about pixels. */
  const [revealed, setRevealed] = useState(false);

  const enter = useCallback(() => setGateOpen(false), [setGateOpen]);

  /**
   * A real `<form>`, so Enter in either field submits — a sign-in page where the return key
   * does nothing is a sign-in page that feels broken. `preventDefault` because there is
   * nowhere to submit to; the handler is the same `enter` both exits call.
   */
  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      enter();
    },
    [enter],
  );

  const overlayRef = useRef<HTMLDivElement | null>(null);
  const emailRef = useRef<HTMLInputElement | null>(null);

  /**
   * The modal half of `aria-modal`: focus in, the document behind out, focus back on close.
   *
   * The gate is a sibling of the whole app under `#root` (`app/App.tsx`), so "the document
   * behind" is precisely this node's siblings — no wrapper element, and therefore no chance of
   * a layout change from adding one.
   */
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!open || !overlay) return;

    const behind = Array.from(overlay.parentElement?.children ?? []).filter(
      (el): el is HTMLElement =>
        el instanceof HTMLElement && el !== overlay && el.getAttribute('role') !== 'status',
    );
    for (const el of behind) el.setAttribute('inert', '');

    const cameFrom = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // Not on a touch screen or in the one-column layout (≤900px, base.css): focusing the
    // field there raises the keyboard and scrolls the art panel out of view before the
    // visitor has seen the page. jsdom has no `matchMedia`, and there the focus stands.
    const handheld =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(pointer: coarse), (max-width: 900px)').matches;
    if (!handheld) emailRef.current?.focus();

    return () => {
      for (const el of behind) el.removeAttribute('inert');
      if (cameFrom?.isConnected) cameFrom.focus();
    };
  }, [open]);

  /**
   * Tab wraps inside the page. `inert` already keeps focus out of the app behind, but it does
   * not stop Tab leaving for the browser's own chrome and coming back somewhere arbitrary —
   * and it is the newer of the two mechanisms, so this is also the floor on a browser without.
   */
  const trapTab = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const stops = Array.from(overlayRef.current?.querySelectorAll<HTMLElement>(TABBABLE) ?? []);
    const first = stops[0];
    const last = stops[stops.length - 1];
    if (!first || !last) return;
    if (document.activeElement !== (e.shiftKey ? first : last)) return;
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
  }, []);

  if (!open) return null;

  return (
    <div
      id="gateOv"
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="gateTitle"
      data-testid="signin-gate"
      onKeyDown={trapTab}
    >
      <div id="gateCard">
        {/* ---------------------------------------------------------------- left: the panel */}
        <section id="gateArt">
          {BACKDROP && <img className="gate-art-photo" src={BACKDROP} alt="" aria-hidden="true" />}

          {/* 2026-10-08: the frosted sheet (title, country pills, lede, the three use tiles, the
              tagline band) is gone — client: "remove the whole soil values geoportal card
              section, place a dark overlay on the image there". What is left is the whole panel
              read as one piece: the photograph, a dark scrim so white text holds its contrast
              over any part of it, and the programme's name with two sentences of standing copy,
              both centred. */}
          <div className="gate-art-scrim" aria-hidden="true" />
          <div className="gate-art-overlay">
            <h1 id="gateTitle" className="gate-art-title">
              {t('gate.title')}
            </h1>
            <p className="gate-art-desc">{t('gate.overviewP1')}</p>
            <p className="gate-art-desc">{t('gate.overviewP2')}</p>
          </div>
        </section>

        {/* ---------------------------------------------------------------- right: the form */}
        <section id="gateForm">
          {/* §F12's EN/FR toggle lives in the topbar — which is now *behind* the first page a
              reader sees. `initialLanguage()` reads a stored preference and otherwise falls
              back to English, so without this a francophone visitor's first screen would be
              English with no control on it. Same behaviour and same storage as the topbar's
              button; the label is the language you are *in*. */}
          <button
            type="button"
            id="gateLang"
            lang={language}
            title={t('topbar.switchLanguage')}
            aria-label={t('topbar.switchLanguage')}
            onClick={() => {
              void setLanguage(otherLanguage(language));
            }}
          >
            {t('topbar.lang')}
          </button>

          <div id="gateFormInner">
            <img className="gate-logo" src={LOGO} alt="" width={132} height={132} />
            <h2 className="gate-welcome">{t('gate.welcome')}</h2>
            <p className="gate-sub">{t('gate.sub')}</p>

            <form onSubmit={onSubmit} noValidate>
              <label className="fld" htmlFor="gateUser">
                {t('gate.email')}
              </label>
              <div className="gate-field">
                <Icon name="mail" size={16} className="gate-field-ico" />
                <input
                  id="gateUser"
                  ref={emailRef}
                  className="std"
                  type="email"
                  name="email"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder={t('gate.emailPh')}
                />
              </div>

              <label className="fld" htmlFor="gatePass">
                {t('gate.password')}
              </label>
              <div className="gate-pass gate-field">
                <Icon name="lock" size={16} className="gate-field-ico" />
                <input
                  id="gatePass"
                  className="std"
                  type={revealed ? 'text' : 'password'}
                  name="password"
                  autoComplete="off"
                  placeholder={t('gate.passwordPh')}
                />
                <button
                  type="button"
                  id="gatePassEye"
                  className="gate-eye"
                  aria-controls="gatePass"
                  aria-pressed={revealed}
                  title={t(revealed ? 'gate.hidePassword' : 'gate.showPassword')}
                  aria-label={t(revealed ? 'gate.hidePassword' : 'gate.showPassword')}
                  onClick={() => setRevealed((v) => !v)}
                >
                  <Icon name={revealed ? 'eyeOff' : 'eye'} size={16} />
                </button>
              </div>

              <div className="gate-row">
                {/*
                  `aria-disabled`, not `disabled`: both controls are in the mock and neither
                  does anything yet, and a control the browser refuses to focus cannot tell a
                  screen-reader user *why*. They are reachable, they announce themselves as
                  unavailable, and the handlers below refuse the interaction rather than
                  pretending to have performed it.
                */}
                <label className="gate-remember" htmlFor="gateRemember">
                  <input
                    type="checkbox"
                    id="gateRemember"
                    aria-disabled="true"
                    title={t('gate.inertControl')}
                    checked={false}
                    onChange={() => undefined}
                  />
                  {t('gate.remember')}
                </label>
                <button
                  type="button"
                  id="gateForgot"
                  className="gate-forgot"
                  aria-disabled="true"
                  title={t('gate.inertControl')}
                  onClick={(e) => e.preventDefault()}
                >
                  {t('gate.forgot')}
                </button>
              </div>

              <button type="submit" className="gate-signin" id="gateSignIn">
                <Icon name="signIn" size={18} />
                {t('gate.signIn')}
              </button>
            </form>

            {/* The mock has no guest door. The portal's data is open and its honest action is
                to browse it, so the door stays — and after the 2026-09-02 review it is a
                full-width button again rather than a line of underlined text. Nobody signing
                in has an account yet, so on today's build *this* is the control that works,
                and a control that works has to be tappable on a phone. It is still second:
                soft-filled and outlined against Sign In's solid blue. */}
            <button type="button" className="gate-guest" id="gateGuest" onClick={enter}>
              <Icon name="user" size={18} />
              {t('gate.guest')}
            </button>

          </div>

          {/*
            The attribution band, arranged as IFDC's brand guide arranges it: one light card
            carrying *Funded by* with the donor mark on the left and *Coalition Partners*
            with the partner grid on the right, and a single dashed rule running the whole
            width under both headings.

            The rule is why this is one three-row grid rather than two columns side by side.
            A border under each heading would be two rules with the gutter's gap punched
            between them; here the headings occupy row 1, the rule is one element spanning
            row 2 from the first column to the last, and the artwork sits in row 3. Every
            child is placed explicitly (see base.css), so the document order below can stay
            the order a screen reader wants — each heading, then the marks it labels, tied
            together by `aria-labelledby` since the grid has separated them visually.

            Every mark is a link to its organisation (`gateAssets.ts` holds the addresses,
            each verified 2026-09-08). `rel="noopener noreferrer"` on all of them: these are
            third-party sites and none of them needs a handle on this window.

            The band is a light card for the reason the two white cards it replaced were:
            several of these logos are gradients or dark type on transparency (SNV, Landscape
            Alliance, IITA); dropped onto a dark ground they would be invisible, and inverting
            them is not ours to do to somebody's brand. Each mark additionally sits on its own
            white chip, which is what makes the "even grid" visible as a grid — the cells are
            identical, and now they look it.

            **It is at the foot of the *right* panel since 2026-09-08**, at the client's
            instruction: *"move the whole logo section to the right, below, as it appears on
            the left — do not change anything else."* So this is the same markup it was, in the
            same order, with the same ids and the same 3 + 5; only its parent changed, from
            `.gate-art-inner` to `#gateForm`.
          */}
          <div className="gate-band" data-gateband="">
            <h2 className="gate-band-title gate-band-funded" id="gateFundedTitle">
              {t('gate.fundedBy')}
            </h2>
            <h2 className="gate-band-title gate-band-coalition" id="gatePartnersTitle">
              {t('gate.partners')}
            </h2>
            <div className="gate-band-rule" data-gatebandrule="" aria-hidden="true" />

            <div
              className="gate-nl"
              data-gatenl=""
              role="group"
              aria-labelledby="gateFundedTitle"
            >
              <a
                className="gate-mark gate-mark-donor"
                data-partner="netherlands"
                data-fit="wide"
                href={NETHERLANDS_HREF}
                target="_blank"
                rel="noopener noreferrer"
              >
                {NETHERLANDS ? (
                  <img src={NETHERLANDS} alt={t('gate.netherlands')} />
                ) : (
                  <span className="gate-mark-text">{t('gate.netherlands')}</span>
                )}
              </a>
            </div>

            <div
              className="gate-partner-row"
              data-gatepartners=""
              role="group"
              aria-labelledby="gatePartnersTitle"
            >
              {/* Two real rows, not one 8-item wrap relying on a forced CSS break: the three
                  big marks (IFDC/SNV/Wageningen) and the five small ones each need their own
                  gap to land on a shared start *and* end edge, and — found the hard way —
                  letting the browser's own flex line-building decide where the wrap falls
                  doesn't reliably keep a row's own items together even when the arithmetic
                  says they should fit. Two explicit flex containers make each row's own
                  wrapping (or lack of it) exactly what its own CSS says, nothing inferred. */}
              <div className="gate-partner-line gate-partner-line-1">
                {PARTNERS.slice(0, 3).map((p) => (
                  <a
                    key={p.slug}
                    className="gate-mark gate-partner"
                    data-partner={p.slug}
                    data-fit={p.fit}
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {p.url ? (
                      <img src={p.url} alt={p.name} />
                    ) : (
                      <span className="gate-mark-text gate-partner-text">{p.name}</span>
                    )}
                  </a>
                ))}
              </div>
              <div className="gate-partner-line gate-partner-line-2">
                {PARTNERS.slice(3).map((p) => (
                  <a
                    key={p.slug}
                    className="gate-mark gate-partner"
                    data-partner={p.slug}
                    data-fit={p.fit}
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {p.url ? (
                      <img src={p.url} alt={p.name} />
                    ) : (
                      <span className="gate-mark-text gate-partner-text">{p.name}</span>
                    )}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

export default SignInGate;
